import { Connect, Message, SocketObservable } from '@genesislcap/foundation-comms';
import { observable } from '@microsoft/fast-element';
import { DI } from '@microsoft/fast-foundation';
import { IRfq, IRfqInsert } from '../types';
import { CONTRACT_TYPE } from '../utils';
import { updateProductTypeCriteriaByNonSEFParticipant } from '../utils/profiles';
import { ICompanyService } from './company.service';

export interface IRfqService {
  openRfqDetailModal(rfqId: string);
  closeRfqDetailModal();
  eventRfqInsert(rfq: IRfqInsert): Promise<any>;
  eventRfqUpdate(rfq: IRfq): Promise<any>;
  eventRfqCancel(rfqid: String, contractType: String): Promise<any>;
  sendRfqCsv(file: any, endpointUrl: any): Promise<any>;
  getAllRfqs(CRITERIA_MATCH: string): SocketObservable<Message>;
  getAllLinkedRfqs(CRITERIA_MATCH: string): SocketObservable<Message>;
  getRfq(rfqId: string): Promise<Message<any>>;
  getRfqVppaNegotiation(vppaId: string): Promise<Message<any>>;
  eventRfqsInsert(rfqObjectsList: Array<IRfqInsert>): Promise<any>;
  getMarketDepthRfqs(CRITERIA_MATCH: string): SocketObservable<Message>;
}

class RfqServiceImpl implements IRfqService {
  constructor(
    @Connect private connect: Connect,
    @ICompanyService private companyService: ICompanyService
  ) {}

  @observable public rfqDetailModalVisibility = false;
  @observable public rfqDetailModelRfqId: string = null;

  public openRfqDetailModal(rfqId: string) {
    this.rfqDetailModelRfqId = rfqId;
    if (this.rfqDetailModelRfqId) {
      this.rfqDetailModalVisibility = true;
    }
  }

  public closeRfqDetailModal() {
    this.rfqDetailModelRfqId = null;
    this.rfqDetailModalVisibility = false;
  }

  public async eventRfqInsert(rfq: IRfqInsert): Promise<any> {
    if (!rfq) return;

    let eventName = 'EVENT_RFQ_INSERT';
    if (rfq.CONTRACT_TYPE === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED) {
      eventName = 'EVENT_RFQ_AUTO_EXECUTE_INSERT';
    }
    const request: any = await this.connect.commitEvent(eventName, {
      DETAILS: rfq,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  public async eventRfqUpdate(rfq: IRfq): Promise<any> {
    if (!rfq) return;

    let eventName = 'EVENT_RFQ_UPDATE';
    if (rfq.CONTRACT_TYPE === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED) {
      eventName = 'EVENT_RFQ_AUTO_EXECUTE_UPDATE';
    }
    const request: any = await this.connect.commitEvent(eventName, {
      DETAILS: rfq,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  public async eventRfqCancel(rfqId: String, contractType: String): Promise<any> {
    if (!rfqId) return;

    let eventName = 'EVENT_RFQ_CANCEL';
    if (contractType === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED) {
      eventName = 'EVENT_RFQ_AUTO_EXECUTE_CANCEL';
    }
    const request: any = await this.connect.commitEvent(eventName, {
      DETAILS: rfqId,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  getAllRfqs(CRITERIA_MATCH: string): SocketObservable<Message<any>> {
    if (this.companyService.isNonSefParticipantAgreement()) {
      CRITERIA_MATCH = updateProductTypeCriteriaByNonSEFParticipant(CRITERIA_MATCH);
    }

    const params = {
      CRITERIA_MATCH,
    };
    const request: any = this.connect.stream(
      'ALL_RFQS',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      params
    );

    return request;
  }

  getAllLinkedRfqs(CRITERIA_MATCH): SocketObservable<Message<any>> {
    const params = {
      CRITERIA_MATCH,
    };
    const request: any = this.connect.stream(
      'NEGOTIATION_RFQS',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      params
    );

    return request;
  }

  getRfq(rfqId: string): Promise<Message<any>> {
    if (!rfqId) return;
    return this.connect.request('RFQ', {
      REQUEST: {
        RFQ_ID: rfqId,
      },
    });
  }

  sendRfqCsv(file: any, endpointUrl: any): Promise<any> {
    const headers = new Headers();
    headers.append('SESSION_AUTH_TOKEN', localStorage.getItem('authToken'));

    const formData = new FormData();
    formData.append(file.fileName, file);

    const settings = {
      method: 'POST',
      body: formData,
      headers: headers,
    };

    return new Promise((resolve, reject) => {
      try {
        resolve(
          fetch(endpointUrl, settings).then((fetchResponse) => {
            return fetchResponse.json();
          })
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  public async eventRfqsInsert(rfqObjectsList: Array<IRfqInsert>): Promise<any> {
    if (!rfqObjectsList) return;

    const request: any = await this.connect.commitEvent('EVENT_RFQS_INSERT', {
      DETAILS: { RFQS_LIST: rfqObjectsList },
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  getRfqVppaNegotiation(vppaId: string): Promise<Message<any>> {
    return this.connect.request('REQ_RFQ_VPPA_NEGOTIATION', {
      REQUEST: {
        VPPA_ID: vppaId,
      },
    });
  }

  getMarketDepthRfqs(CRITERIA_MATCH: string): SocketObservable<Message<any>> {
    if (this.companyService.isNonSefParticipantAgreement()) {
      CRITERIA_MATCH = updateProductTypeCriteriaByNonSEFParticipant(CRITERIA_MATCH, 'RFQ_');
    }
    const params = {
      CRITERIA_MATCH,
    };
    const request: any = this.connect.stream(
      'MARKET_DEPTH_RFQS',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      params
    );

    return request;
  }
}

export const IRfqService = DI.createInterface<IRfqService>((x) => x.singleton(RfqServiceImpl));
